import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Tabs, Tab, Table, TableHead, TableRow,
  TableCell, TableBody, Chip, CircularProgress, TextField, MenuItem, Button, Stack,
  ToggleButtonGroup, ToggleButton, Tooltip,
} from '@mui/material';
import { Assessment, Download } from '@mui/icons-material';
import api from '../../services/api';

const num = (v: any, d = 4) => (v === null || v === undefined || v === '') ? '—' : Number(v).toFixed(d);
const money = (v: any) => (v === null || v === undefined || v === '') ? '—' : '$' + Number(v).toFixed(4);
const mins = (s: any) => (Number(s || 0) / 60).toFixed(1);
const pct = (v: any) => (v === null || v === undefined || v === '') ? '—' : Number(v).toFixed(2) + '%';

function downloadCsv(name: string, rows: any[], cols: { key: string; label: string }[]) {
  const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.map(c => esc(c.label)).join(',')]
    .concat(rows.map(r => cols.map(c => esc(r[c.key])).join(','))).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a'); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

const marginColor = (v: any) => Number(v) < 0 ? 'error.main' : Number(v) === 0 ? 'warning.main' : 'success.main';

const LEAK_TYPES = [
  { key: 'negative', label: 'Negative margin' },
  { key: 'zero_sell', label: 'Zero-sell (billed $0)' },
  { key: 'unrated', label: 'Unrated' },
  { key: 'no_buy', label: 'Missing buy rate' },
];

export const RevenueAssurancePage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const iso30 = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
  const [from, setFrom] = useState(iso30);
  const [to, setTo] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState<any>(null);
  const [leakType, setLeakType] = useState('negative');
  const [leak, setLeak] = useState<any>(null);
  const [group, setGroup] = useState('customer');
  const [byData, setByData] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [drill, setDrill] = useState<any>(null); // { plan, rows }
  const [problemsOnly, setProblemsOnly] = useState(true);

  useEffect(() => { api.get('/tenants?limit=500').then(r => setTenants(r.data?.data || r.data || [])).catch(() => {}); }, []);

  const qs = () => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to + 'T23:59:59');
    if (tenantId) p.set('tenant_id', tenantId);
    return p.toString();
  };

  const load = () => {
    setLoading(true);
    const done = () => setLoading(false);
    if (tab === 0) api.get(`/cdr/revenue/summary?${qs()}`).then(r => setSummary(r.data)).catch(() => setSummary(null)).finally(done);
    else if (tab === 1) api.get(`/cdr/revenue/leakage?type=${leakType}&${qs()}`).then(r => setLeak(r.data)).catch(() => setLeak(null)).finally(done);
    else if (tab === 2) api.get(`/cdr/revenue/by?group=${group}&${qs()}`).then(r => setByData(r.data)).catch(() => setByData(null)).finally(done);
    else if (tab === 3) {
      if (drill) api.get(`/cdr/revenue/rate-card-audit?plan_id=${drill.plan.id}&problems=${problemsOnly}`).then(r => setDrill({ plan: drill.plan, rows: r.data.rows })).catch(() => {}).finally(done);
      else api.get('/cdr/revenue/rate-card-audit').then(r => setPlans(r.data.plans || [])).catch(() => setPlans([])).finally(done);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab, from, to, tenantId, leakType, group, problemsOnly, drill?.plan?.id]);

  const KPI = ({ label, value, color, onClick, sub }: any) => (
    <Grid item xs={6} sm={4} md={3}>
      <Card variant="outlined" sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
        <CardContent sx={{ py: 1.5 }}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h5" fontWeight={800} color={color || 'text.primary'}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Assessment color="primary" />
        <Typography variant="h5" fontWeight={800}>Revenue Assurance</Typography>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={e => setFrom(e.target.value)} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={e => setTo(e.target.value)} helperText="blank = up to now" />
        <TextField select size="small" label="Customer" value={tenantId} onChange={e => setTenantId(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All customers</MenuItem>
          {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
        <Button size="small" onClick={load}>Refresh</Button>
        {(tab === 3) && <Typography variant="caption" color="text.secondary">(rate-card audit ignores date/customer — it inspects the cards themselves)</Typography>}
      </Stack>

      <Card>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setDrill(null); }} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Overview" />
          <Tab label="Call Leakage" />
          <Tab label="Margin by Customer / Route / Country" />
          <Tab label="Rate Card Audit" />
        </Tabs>
        <CardContent>
          {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
            <>
              {/* ── Overview ── */}
              {tab === 0 && summary && (
                <Grid container spacing={1.5}>
                  <KPI label="Total calls" value={summary.total_calls} />
                  <KPI label="Answered" value={summary.answered} />
                  <KPI label="Billable minutes" value={mins(summary.billable_sec)} />
                  <KPI label="Blended margin" value={pct(summary.margin_pct)} color={marginColor(summary.margin)} />
                  <KPI label="Revenue (sell)" value={money(summary.revenue)} color="success.main" />
                  <KPI label="Cost (buy)" value={money(summary.cost)} />
                  <KPI label="Margin (profit)" value={money(summary.margin)} color={marginColor(summary.margin)} />
                  <KPI label="Lost to negative margin" value={money(summary.negative_margin_value)} color="error.main" />
                  <Grid item xs={12}><Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1 }}>Leakage signals (click to inspect)</Typography></Grid>
                  <KPI label="Negative-margin calls" value={summary.negative_margin} color={summary.negative_margin ? 'error.main' : 'text.primary'} onClick={() => { setLeakType('negative'); setTab(1); }} />
                  <KPI label="Zero-sell billed calls" value={summary.zero_sell} color={summary.zero_sell ? 'error.main' : 'text.primary'} onClick={() => { setLeakType('zero_sell'); setTab(1); }} />
                  <KPI label="Unrated calls" value={summary.unrated} color={summary.unrated ? 'error.main' : 'text.primary'} onClick={() => { setLeakType('unrated'); setTab(1); }} />
                  <KPI label="Missing buy rate" value={summary.no_buy} color={summary.no_buy ? 'warning.main' : 'text.primary'} onClick={() => { setLeakType('no_buy'); setTab(1); }} />
                </Grid>
              )}

              {/* ── Call Leakage ── */}
              {tab === 1 && (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                    <ToggleButtonGroup size="small" exclusive value={leakType} onChange={(_, v) => v && setLeakType(v)}>
                      {LEAK_TYPES.map(t => <ToggleButton key={t.key} value={t.key}>{t.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Button size="small" startIcon={<Download />} disabled={!leak?.data?.length}
                      onClick={() => downloadCsv(`leakage-${leakType}.csv`, leak.data, [
                        { key: 'start_time', label: 'Time' }, { key: 'customer', label: 'Customer' }, { key: 'dst_number', label: 'Destination' },
                        { key: 'carrier', label: 'Carrier' }, { key: 'billable_duration', label: 'Billable s' }, { key: 'buy_rate', label: 'Buy rate' },
                        { key: 'sell_rate', label: 'Sell rate' }, { key: 'buy_cost', label: 'Buy cost' }, { key: 'sell_cost', label: 'Sell cost' }, { key: 'margin', label: 'Margin' },
                      ])}>Export CSV</Button>
                  </Stack>
                  {leak && <Typography variant="caption" color="text.secondary">{leak.count} call(s){leak.count >= 5000 && ' (capped at 5000)'}</Typography>}
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead><TableRow>{['Time', 'Customer', 'Destination', 'Carrier', 'Bill s', 'Buy rate', 'Sell rate', 'Buy cost', 'Sell cost', 'Margin'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                      <TableBody>
                        {(leak?.data || []).map((r: any, i: number) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{new Date(r.start_time).toLocaleString()}</TableCell>
                            <TableCell>{r.customer}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{r.dst_number}</TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>{r.carrier}</TableCell>
                            <TableCell>{r.billable_duration}</TableCell>
                            <TableCell>{num(r.buy_rate, 6)}</TableCell>
                            <TableCell sx={{ color: Number(r.sell_rate) === 0 ? 'error.main' : undefined }}>{num(r.sell_rate, 6)}</TableCell>
                            <TableCell>{money(r.buy_cost)}</TableCell>
                            <TableCell>{money(r.sell_cost)}</TableCell>
                            <TableCell sx={{ color: marginColor(r.margin), fontWeight: 600 }}>{money(r.margin)}</TableCell>
                          </TableRow>
                        ))}
                        {leak && leak.data.length === 0 && <TableRow><TableCell colSpan={10} align="center" sx={{ py: 5, color: 'success.main' }}>✓ No calls of this type in range.</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}

              {/* ── Margin by dimension ── */}
              {tab === 2 && (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                    <ToggleButtonGroup size="small" exclusive value={group} onChange={(_, v) => v && setGroup(v)}>
                      <ToggleButton value="customer">By Customer</ToggleButton>
                      <ToggleButton value="carrier">By Carrier / Route</ToggleButton>
                      <ToggleButton value="country">By Country</ToggleButton>
                    </ToggleButtonGroup>
                    <Button size="small" startIcon={<Download />} disabled={!byData?.data?.length}
                      onClick={() => downloadCsv(`margin-by-${group}.csv`, byData.data, [
                        { key: 'name', label: group }, { key: 'attempted', label: 'Calls' }, { key: 'answered', label: 'Answered' }, { key: 'billable_sec', label: 'Billable s' },
                        { key: 'revenue', label: 'Revenue' }, { key: 'cost', label: 'Cost' }, { key: 'margin', label: 'Margin' }, { key: 'margin_pct', label: 'Margin %' },
                        { key: 'neg_calls', label: 'Neg-margin calls' }, { key: 'zero_sell_calls', label: 'Zero-sell calls' },
                      ])}>Export CSV</Button>
                  </Stack>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead><TableRow>{[group === 'customer' ? 'Customer' : group === 'carrier' ? 'Carrier / Route' : 'Country', 'Calls', 'Ans', 'Min', 'Revenue', 'Cost', 'Margin', 'Margin %', 'Neg', 'Zero-sell'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                      <TableBody>
                        {(byData?.data || []).map((r: any, i: number) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                            <TableCell>{r.attempted}</TableCell>
                            <TableCell>{r.answered}</TableCell>
                            <TableCell>{mins(r.billable_sec)}</TableCell>
                            <TableCell>{money(r.revenue)}</TableCell>
                            <TableCell>{money(r.cost)}</TableCell>
                            <TableCell sx={{ color: marginColor(r.margin), fontWeight: 600 }}>{money(r.margin)}</TableCell>
                            <TableCell sx={{ color: marginColor(r.margin) }}>{pct(r.margin_pct)}</TableCell>
                            <TableCell>{Number(r.neg_calls) > 0 ? <Chip size="small" color="error" label={r.neg_calls} /> : '—'}</TableCell>
                            <TableCell>{Number(r.zero_sell_calls) > 0 ? <Chip size="small" color="warning" label={r.zero_sell_calls} /> : '—'}</TableCell>
                          </TableRow>
                        ))}
                        {byData?.totals && (
                          <TableRow sx={{ '& td': { fontWeight: 800, borderTop: '2px solid', borderColor: 'divider' } }}>
                            <TableCell>TOTAL</TableCell>
                            <TableCell>{byData.totals.attempted}</TableCell>
                            <TableCell>{byData.totals.answered}</TableCell>
                            <TableCell>{mins(byData.totals.billable_sec)}</TableCell>
                            <TableCell>{money(byData.totals.revenue)}</TableCell>
                            <TableCell>{money(byData.totals.cost)}</TableCell>
                            <TableCell sx={{ color: marginColor(byData.totals.margin) }}>{money(byData.totals.margin)}</TableCell>
                            <TableCell>{pct(byData.totals.margin_pct)}</TableCell>
                            <TableCell>{byData.totals.neg_calls}</TableCell>
                            <TableCell>{byData.totals.zero_sell_calls}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}

              {/* ── Rate Card Audit ── */}
              {tab === 3 && !drill && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead><TableRow>{['Rate plan', 'Type', 'Customers using it', 'Destinations', 'Missing buy', 'Sell ≤ buy', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                    <TableBody>
                      {plans.map(p => {
                        const inUse = Number(p.customers) > 0;
                        const badMargin = Number(p.non_positive_margin) > 0 && inUse;
                        return (
                          <TableRow key={p.id} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                            <TableCell><Chip size="small" variant="outlined" label={p.plan_type || '—'} color={p.plan_type === 'retail' ? 'primary' : 'default'} /></TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{inUse ? <Tooltip title={p.customer_names || ''}><span>{p.customers} — {(p.customer_names || '').slice(0, 40)}{(p.customer_names || '').length > 40 ? '…' : ''}</span></Tooltip> : <em style={{ color: '#999' }}>not assigned</em>}</TableCell>
                            <TableCell>{p.destinations}</TableCell>
                            <TableCell>{Number(p.no_buy) > 0 ? <Chip size="small" color="warning" label={p.no_buy} /> : '—'}</TableCell>
                            <TableCell>{Number(p.non_positive_margin) > 0 ? <Chip size="small" color={badMargin ? 'error' : 'default'} variant={badMargin ? 'filled' : 'outlined'} label={p.non_positive_margin} /> : '—'}</TableCell>
                            <TableCell><Button size="small" onClick={() => { setProblemsOnly(true); setDrill({ plan: p, rows: null }); }}>Inspect</Button></TableCell>
                          </TableRow>
                        );
                      })}
                      {plans.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>No rate plans.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    “Sell ≤ buy” is only flagged red for plans actually assigned to a customer. Wholesale “Buy Rates” plans hold cost only, so sell = buy there is expected.
                  </Typography>
                </Box>
              )}

              {tab === 3 && drill && (
                <>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                    <Button size="small" onClick={() => setDrill(null)}>← All plans</Button>
                    <Typography variant="subtitle1" fontWeight={700}>{drill.plan.name}</Typography>
                    <ToggleButtonGroup size="small" exclusive value={problemsOnly ? 'p' : 'a'} onChange={(_, v) => v && setProblemsOnly(v === 'p')}>
                      <ToggleButton value="p">Problems only</ToggleButton>
                      <ToggleButton value="a">All destinations</ToggleButton>
                    </ToggleButtonGroup>
                    <Button size="small" startIcon={<Download />} disabled={!drill.rows?.length}
                      onClick={() => downloadCsv(`ratecard-${drill.plan.name}.csv`, drill.rows, [
                        { key: 'destination', label: 'Prefix' }, { key: 'destination_name', label: 'Destination' }, { key: 'carrier', label: 'Carrier' },
                        { key: 'buy_rate', label: 'Buy' }, { key: 'sell_rate', label: 'Sell' }, { key: 'margin', label: 'Margin' }, { key: 'margin_pct', label: 'Margin %' },
                      ])}>Export CSV</Button>
                  </Stack>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead><TableRow>{['Prefix', 'Destination', 'Carrier', 'Buy', 'Sell', 'Margin', 'Margin %'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                      <TableBody>
                        {(drill.rows || []).map((r: any, i: number) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{r.destination}</TableCell>
                            <TableCell>{r.destination_name || '—'}</TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>{r.carrier}</TableCell>
                            <TableCell>{num(r.buy_rate, 6)}</TableCell>
                            <TableCell>{num(r.sell_rate, 6)}</TableCell>
                            <TableCell sx={{ color: marginColor(r.margin), fontWeight: 600 }}>{num(r.margin, 6)}</TableCell>
                            <TableCell sx={{ color: marginColor(r.margin) }}>{pct(r.margin_pct)}</TableCell>
                          </TableRow>
                        ))}
                        {drill.rows && drill.rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'success.main' }}>✓ No margin problems on this plan.</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
